/**
 * WebSocket contract: room helpers, client ops and the discriminated union of every
 * server→client event listed under `x-websocket-events` in the OpenAPI file.
 */
import { z } from 'zod';
import {
  ActorRef,
  Timestamp,
  WsAck,
  WsAssetCreatedPayload,
  WsAssetMovedPayload,
  WsAssetProcessingPayload,
  WsAssetRemovedPayload,
  WsAssetMergedPayload,
  WsAssetUpdatedPayload,
  WsBoardArchivedPayload,
  WsBoardDeletedPayload,
  WsBoardViewPayload,
  WsBoardPayload,
  WsClientMessage,
  WsCommentDeletedPayload,
  WsCommentPayload,
  WsCoverChangedPayload,
  WsFieldPolicyPayload,
  WsNotificationPayload,
  WsNotificationReadPayload,
  WsPinPayload,
  WsPresetChangedPayload,
  WsPresencePayload,
  WsRetentionAppliedPayload,
  WsRightsExpiredPayload,
  WsTagGroupPayload,
  WsTagMergedPayload,
  WsTrashPurgedPayload,
  WsRoom,
  WsUploadPayload,
  WsVersionPayload,
  WsVersionStateChangedPayload,
  WsWorkspacePayload,
  WsWorkspacePolicyPayload,
  WS_ROOM_PATTERN,
  type WsRoomKind,
} from './schemas/index.js';

// ---------------------------------------------------------------------------
// Rooms
// ---------------------------------------------------------------------------

export type WsRoomName = `${WsRoomKind}:${string}`;

export function roomName(kind: WsRoomKind, id: string): WsRoomName {
  return `${kind}:${id}`;
}
export const workspaceRoom = (workspaceId: string): WsRoomName =>
  roomName('workspace', workspaceId);
export const boardRoom = (boardId: string): WsRoomName => roomName('board', boardId);
export const assetRoom = (assetId: string): WsRoomName => roomName('asset', assetId);

export function parseRoom(room: string): { kind: WsRoomKind; id: string } | null {
  if (!WS_ROOM_PATTERN.test(room)) return null;
  const idx = room.indexOf(':');
  return { kind: room.slice(0, idx) as WsRoomKind, id: room.slice(idx + 1) };
}

// ---------------------------------------------------------------------------
// Protocol constants (x-websocket-events.endpoint / replay)
// ---------------------------------------------------------------------------

export const WS_PATH = '/ws';
export const WS_TOKEN_TTL_SECONDS = 60;
export const WS_SERVER_PING_INTERVAL_MS = 30_000;
export const WS_PRESENCE_HEARTBEAT_MS = 15_000;
export const WS_PRESENCE_STALE_MS = 45_000;
export const WS_CLIENT_RATE_LIMIT_PER_MIN = 60;
export const WS_REPLAY_RETENTION_HOURS = 24;

/**
 * Replay streams (`events:{room}` in Redis, entry id `{seq}-0` with seq = `event_outbox.seq`). The relay caps every
 * stream on XADD; the worker's `trim-event-streams` job trims to the same cap, drops entries below the outbox
 * watermark and deletes streams left empty. Asset rooms get a much smaller cap: a processing run emits at most
 * ~25 `version.state_changed` events there (progress moves in ≥5 % steps), each carrying the serialised version,
 * so 200 entries hold several runs plus comment traffic. A client that missed more than that gets
 * `replay_truncated` and refetches the asset, its comments and versions, which is cheaper than replaying hundreds
 * of stale progress frames.
 */
export const WS_STREAM_MAXLEN = 10_000;
export const WS_ASSET_STREAM_MAXLEN = 200;
export const wsStreamMaxLen = (room: string): number =>
  room.startsWith('asset:') ? WS_ASSET_STREAM_MAXLEN : WS_STREAM_MAXLEN;
/**
 * Oldest `event_outbox.seq` still retained, written by `trim-event-streams` before it trims. Replay of a room whose
 * stream is gone uses it as the window start (`last_event_id` below it is a gap). Kept outside `events:*` so stream
 * scans never see it.
 */
export const WS_STREAM_MIN_SEQ_KEY = 'events-retention:min-seq';

/** Server close codes. */
export const WS_CLOSE_CODES = {
  INVALID_TOKEN: 4401,
  ROOM_DENIED: 4403,
  RATE_LIMITED: 4429,
} as const;

// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------

export const WsSubscribeOp = z.object({
  op: z.literal('subscribe'),
  rooms: z.array(WsRoom).min(1),
  last_event_id: z.string().nullable().optional(),
  req_id: z.string().optional(),
});
export const WsUnsubscribeOp = z.object({
  op: z.literal('unsubscribe'),
  rooms: z.array(WsRoom).min(1),
  req_id: z.string().optional(),
});
export const WsPresenceHeartbeatOp = z.object({
  op: z.literal('presence.heartbeat'),
  presence: z.object({
    room: WsRoom,
    playhead_ms: z.int().nullable().optional(),
    version_id: z.uuid().nullable().optional(),
  }),
  req_id: z.string().optional(),
});
export const WsPingOp = z.object({
  op: z.literal('ping'),
  req_id: z.string().optional(),
});

/** Strict per-op client message (narrower than the generic `WsClientMessage` component). */
export const WsClientOpMessage = z.discriminatedUnion('op', [
  WsSubscribeOp,
  WsUnsubscribeOp,
  WsPresenceHeartbeatOp,
  WsPingOp,
]);
export type WsClientOpMessage = z.infer<typeof WsClientOpMessage>;

// ---------------------------------------------------------------------------
// Server → client events
// ---------------------------------------------------------------------------

/** Event type → payload schema, exactly the 54 entries of `x-websocket-events.events`. */
export const WS_EVENT_PAYLOADS = {
  // board room
  'asset.created': WsAssetCreatedPayload,
  'asset.updated': WsAssetUpdatedPayload,
  'asset.removed': WsAssetRemovedPayload,
  'asset.moved': WsAssetMovedPayload,
  'asset.deleted': WsAssetRemovedPayload,
  'asset.restored': WsAssetCreatedPayload,
  'asset.processing': WsAssetProcessingPayload,
  'board.created': WsBoardPayload,
  'board.updated': WsBoardPayload,
  'board.moved': WsBoardPayload,
  'board.deleted': WsBoardDeletedPayload,
  'board.restored': WsBoardPayload,
  'cover.changed': WsCoverChangedPayload,
  'upload.completed': WsUploadPayload,
  'upload.aborted': WsUploadPayload,
  // asset room
  'comment.created': WsCommentPayload,
  'comment.updated': WsCommentPayload,
  'comment.deleted': WsCommentDeletedPayload,
  'comment.resolved': WsCommentPayload,
  'comment.unresolved': WsCommentPayload,
  'annotation.created': WsCommentPayload,
  'annotation.updated': WsCommentPayload,
  'annotation.deleted': WsCommentPayload,
  'version.created': WsVersionPayload,
  'version.state_changed': WsVersionStateChangedPayload,
  'version.activated': WsVersionPayload,
  'version.archived': WsVersionPayload,
  'version.unarchived': WsVersionPayload,
  'version.deleted': WsVersionPayload,
  'presence.update': WsPresencePayload,
  // workspace room
  'workspace.updated': WsWorkspacePayload,
  'member.updated': WsWorkspacePayload,
  'member.removed': WsWorkspacePayload,
  'custom_field.changed': WsWorkspacePayload,
  'tag.changed': WsWorkspacePayload,
  // Release 1 "Find and tidy" (API-emitted, full payloads)
  'view.created': WsBoardViewPayload,
  'view.updated': WsBoardViewPayload,
  'view.deleted': WsBoardViewPayload,
  'pin.changed': WsPinPayload,
  'asset.archived': WsAssetUpdatedPayload,
  'asset.unarchived': WsAssetUpdatedPayload,
  'board.archived': WsBoardArchivedPayload,
  'board.unarchived': WsBoardArchivedPayload,
  'asset.merged': WsAssetMergedPayload,
  'tag.merged': WsTagMergedPayload,
  'trash.purged': WsTrashPurgedPayload,
  // Release 2 "Structure and governance" (rights.expired / retention.applied are SQL-emitted by the nightly jobs)
  'field_policy.changed': WsFieldPolicyPayload,
  'tag_group.changed': WsTagGroupPayload,
  'workspace_policy.changed': WsWorkspacePolicyPayload,
  'rights.expired': WsRightsExpiredPayload,
  'retention.applied': WsRetentionAppliedPayload,
  // Release 3 "Notify, deliver, see" (notification.* are member-scoped like pin.changed; notification.created is SQL-emitted)
  'notification.created': WsNotificationPayload,
  'notification.read': WsNotificationReadPayload,
  'preset.changed': WsPresetChangedPayload,
} as const;

export type WsEventType = keyof typeof WS_EVENT_PAYLOADS;
export const WS_EVENT_TYPES = Object.keys(WS_EVENT_PAYLOADS) as readonly WsEventType[];

/** Rooms each event is published to (from the spec's `room` column). */
export const WS_EVENT_ROOMS: Readonly<Record<WsEventType, readonly WsRoomKind[]>> = {
  'asset.created': ['board'],
  'asset.updated': ['board', 'asset'],
  'asset.removed': ['board'],
  'asset.moved': ['board'],
  'asset.deleted': ['board', 'asset'],
  'asset.restored': ['board', 'asset'],
  'asset.processing': ['board'],
  'board.created': ['board', 'workspace'],
  'board.updated': ['board'],
  'board.moved': ['board'],
  'board.deleted': ['board', 'workspace'],
  'board.restored': ['board', 'workspace'],
  'cover.changed': ['board'],
  'upload.completed': ['board'],
  'upload.aborted': ['board'],
  'comment.created': ['asset'],
  'comment.updated': ['asset'],
  'comment.deleted': ['asset'],
  'comment.resolved': ['asset'],
  'comment.unresolved': ['asset'],
  'annotation.created': ['asset'],
  'annotation.updated': ['asset'],
  'annotation.deleted': ['asset'],
  'version.created': ['asset'],
  'version.state_changed': ['asset'],
  'version.activated': ['asset'],
  'version.archived': ['asset'],
  'version.unarchived': ['asset'],
  'version.deleted': ['asset'],
  'presence.update': ['asset', 'board'],
  'workspace.updated': ['workspace'],
  'member.updated': ['workspace'],
  'member.removed': ['workspace'],
  'custom_field.changed': ['workspace'],
  'tag.changed': ['workspace'],
  'view.created': ['board'],
  'view.updated': ['board'],
  'view.deleted': ['board'],
  'pin.changed': ['workspace'],
  'asset.archived': ['board', 'asset'],
  'asset.unarchived': ['board', 'asset'],
  'board.archived': ['board', 'workspace'],
  'board.unarchived': ['board', 'workspace'],
  'asset.merged': ['board', 'asset'],
  'tag.merged': ['workspace'],
  'trash.purged': ['workspace'],
  'field_policy.changed': ['workspace'],
  'tag_group.changed': ['workspace'],
  'workspace_policy.changed': ['workspace'],
  'rights.expired': ['workspace'],
  'retention.applied': ['workspace'],
  'notification.created': ['workspace'],
  'notification.read': ['workspace'],
  'preset.changed': ['workspace'],
};

/** Reserved in v1: never emitted standalone; annotations ride inside `comment.*` payloads. */
export const WS_RESERVED_EVENT_TYPES: readonly WsEventType[] = [
  'annotation.created',
  'annotation.updated',
  'annotation.deleted',
];

/** Events delivered only to the connections of the member named in the payload (`member_id` / view owner). */
export const WS_MEMBER_SCOPED_EVENT_TYPES: readonly WsEventType[] = ['pin.changed', 'notification.created', 'notification.read'];

/** Events that are not persisted to the stream and never replayed. */
export const WS_EPHEMERAL_EVENT_TYPES: readonly WsEventType[] = ['presence.update'];

const envelopeBase = {
  id: z.string(),
  ts: Timestamp,
  room: WsRoom,
  actor: ActorRef,
};

function eventSchema<T extends WsEventType>(type: T) {
  return z.object({ ...envelopeBase, type: z.literal(type), payload: WS_EVENT_PAYLOADS[type] });
}

/** Typed envelope for one event type. */
export type WsEvent<T extends WsEventType = WsEventType> = {
  id: string;
  ts: string;
  room: string;
  type: T;
  actor: ActorRef;
  payload: z.infer<(typeof WS_EVENT_PAYLOADS)[T]>;
};

/** Discriminated union (on `type`) of all 54 server events with their payload schemas. */
export const WsServerEvent = z.discriminatedUnion('type', [
  eventSchema('asset.created'),
  eventSchema('asset.updated'),
  eventSchema('asset.removed'),
  eventSchema('asset.moved'),
  eventSchema('asset.deleted'),
  eventSchema('asset.restored'),
  eventSchema('asset.processing'),
  eventSchema('board.created'),
  eventSchema('board.updated'),
  eventSchema('board.moved'),
  eventSchema('board.deleted'),
  eventSchema('board.restored'),
  eventSchema('cover.changed'),
  eventSchema('upload.completed'),
  eventSchema('upload.aborted'),
  eventSchema('comment.created'),
  eventSchema('comment.updated'),
  eventSchema('comment.deleted'),
  eventSchema('comment.resolved'),
  eventSchema('comment.unresolved'),
  eventSchema('annotation.created'),
  eventSchema('annotation.updated'),
  eventSchema('annotation.deleted'),
  eventSchema('version.created'),
  eventSchema('version.state_changed'),
  eventSchema('version.activated'),
  eventSchema('version.archived'),
  eventSchema('version.unarchived'),
  eventSchema('version.deleted'),
  eventSchema('presence.update'),
  eventSchema('workspace.updated'),
  eventSchema('member.updated'),
  eventSchema('member.removed'),
  eventSchema('custom_field.changed'),
  eventSchema('tag.changed'),
  eventSchema('view.created'),
  eventSchema('view.updated'),
  eventSchema('view.deleted'),
  eventSchema('pin.changed'),
  eventSchema('asset.archived'),
  eventSchema('asset.unarchived'),
  eventSchema('board.archived'),
  eventSchema('board.unarchived'),
  eventSchema('asset.merged'),
  eventSchema('tag.merged'),
  eventSchema('trash.purged'),
  eventSchema('field_policy.changed'),
  eventSchema('tag_group.changed'),
  eventSchema('workspace_policy.changed'),
  eventSchema('rights.expired'),
  eventSchema('retention.applied'),
  eventSchema('notification.created'),
  eventSchema('notification.read'),
  eventSchema('preset.changed'),
]);
export type WsServerEvent = z.infer<typeof WsServerEvent>;

/** Any server→client frame: a typed event envelope or an ack/error/pong. */
export const WsServerFrame = z.union([WsServerEvent, WsAck]);
export type WsServerFrame = z.infer<typeof WsServerFrame>;

export function isWsAck(frame: WsServerFrame): frame is WsAck {
  return 'op' in frame;
}

export function parseWsClientMessage(raw: unknown): WsClientMessage {
  return WsClientMessage.parse(raw);
}
export type { WsClientMessage, WsAck };
